import React from 'react';
import {
  Box,
  ListItem,
  Typography,
  Paper,
  Chip,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
} from '@mui/material';

// Helper function to format currency
const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

// Component to render AI labels
const AILabelsSection = ({ aiLabels }) => {
  if (!aiLabels || aiLabels.length === 0) return null;

  const getLabelColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'secondary';
      case 'med':
        return 'error';
      case 'low':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        AI Labels
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {aiLabels.map((label) => (
          <Chip
            key={label.id}
            label={label.id}
            color={getLabelColor(label.confidence)}
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    </Box>
  );
};

// Component to render market info header
const MarketInfoSection = ({ market, hit }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {market.question || 'No question'}
          </Typography>
          {market.description && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
              "{market.description}"
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={hit._score != null ? hit._score.toFixed(2) : '-'}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {market.image_url && (
          <Avatar
            alt="Market"
            src={market.image_url}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: 'grey.200',
              border: '2px solid',
              borderColor: 'primary.main',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        {market.status && (
          <Chip label={market.status} size="small" variant="filled" color="info" />
        )}
        {market.active !== undefined && (
          <Chip
            label={market.active ? 'Active' : 'Inactive'}
            size="small"
            variant="filled"
            color={market.active ? 'success' : 'default'}
          />
        )}
        {market.closed !== undefined && (
          <Chip
            label={market.closed ? 'Closed' : 'Open'}
            size="small"
            variant="filled"
            color={market.closed ? 'error' : 'success'}
          />
        )}
        {market.ticker && (
          <Chip label={market.ticker} size="small" variant="outlined" />
        )}
      </Box>
    </Box>
  );
};

// Component to render volume and metrics
const MarketMetricsSection = ({ market }) => {
  const metricsData = [
    { label: 'Volume', value: formatCurrency(market.volume) },
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Metrics
      </Typography>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {metricsData.map((row, idx) => (
          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: idx < metricsData.length - 1 ? 0.5 : 0 }}>
            <Typography variant="body2" color="text.secondary">
              {row.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

// Component to render dates
const DatesSection = ({ market }) => {
  const dateFields = [
    { key: 'created_at', label: 'Created' },
    { key: 'updated_at', label: 'Updated' },
    { key: 'start_date', label: 'Start Date' },
    { key: 'end_date', label: 'End Date' },
  ];

  const hasAnyDate = dateFields.some(({ key }) => market[key]);

  if (!hasAnyDate) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Dates
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {dateFields.map(
          ({ key, label }) =>
            market[key] && (
              <Paper
                key={key}
                elevation={0}
                sx={{
                  p: 1,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                  {formatDate(market[key])}
                </Typography>
              </Paper>
            )
        )}
      </Box>
    </Box>
  );
};

// Component to render document info
const DocumentInfoSection = ({ hit, market }) => {
  const marketId = market.item_id || hit._id;
  const ticker = market.ticker;

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          wordBreak: 'break-all',
          fontSize: '0.7rem',
        }}
      >
        #{marketId}
        {ticker ? ` (${ticker})` : ''}
      </Typography>
    </Box>
  );
};

const KalshiItemCard = ({ hit }) => {
  const { _source: market } = hit;
  const marketUrl = market?.market_url;

  // Build AI labels (same structure as PolymarketItemCard)
  const aiLabels_high = market.ai_labels_high || [];
  const aiLabels_med = market.ai_labels_med || [];
  const aiLabels_low = market.ai_labels_low || [];
  const aiLabelsMap = {};

  for (const label of aiLabels_high) {
    aiLabelsMap[label] = 'high';
  }
  for (const label of aiLabels_med) {
    if (!(label in aiLabelsMap)) {
      aiLabelsMap[label] = 'med';
    }
  }
  for (const label of aiLabels_low) {
    if (!(label in aiLabelsMap)) {
      aiLabelsMap[label] = 'low';
    }
  }
  const aiLabels = Object.keys(aiLabelsMap).map((label) => ({
    id: label,
    confidence: aiLabelsMap[label],
  }));

  const cardBody = (
    <>
      {market.image_url && (
        <Box
          component="img"
          src={market.image_url}
          alt="Market cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
          sx={{
            width: '100%',
            height: 200,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      )}
      <CardContent sx={{ pb: 2 }}>
        <MarketInfoSection market={market} hit={hit} />
        <AILabelsSection aiLabels={aiLabels} />
        <MarketMetricsSection market={market} />
        <DatesSection market={market} />
        <DocumentInfoSection hit={hit} market={market} />
      </CardContent>
    </>
  );

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0,
      }}
    >
      <Card
        elevation={1}
        sx={{
          width: '100%',
          backgroundColor: '#fafafa',
          overflow: 'visible',
        }}
      >
        {marketUrl ? (
          <CardActionArea
            component="a"
            href={marketUrl}
            aria-label="Open Kalshi market"
            sx={{
              alignItems: 'stretch',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            {cardBody}
          </CardActionArea>
        ) : (
          <Box sx={{ width: '100%' }}>
            {cardBody}
          </Box>
        )}
      </Card>
    </ListItem>
  );
};

export default KalshiItemCard;
